import React, { useRef, useEffect, useState } from "react";
import {
  Typography,
  Grid,
  Box,
  TextField,
  Collapse,
  Paper,
  Stack,
  CircularProgress,
  Container,
  Menu,
  Button,
  Tooltip,
  alpha,
  Tabs,
  Tab,
  useMediaQuery,
  MenuItem,
  IconButton,
} from "@mui/material";
import ThemeSelectorButtons from "./ThemeSelectorButtons";
import FileUploadIcon from "@mui/icons-material/FileUpload";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditIcon from "@mui/icons-material/Edit";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "./SnackbarHandler";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SubjectIcon from "@mui/icons-material/Subject";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { BookPlanSelector } from "./BookPlanSelector";
import SourceDisplay from "./SourceDisplay";
import VideoGuideDialog from "./VideoGuideDialog";
import { Link } from "react-router-dom";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisionSimple from "./VisionSimple";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import {
  lessonMapping,
  getLessonTypeParam,
  lessonUrlMapping,
} from "./functions";
import { Link as LinkIcon } from "@mui/icons-material";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { AnimatedStep, AnimatedText, SmoothTransitionHint } from "./Animations";

import SchoolIcon from "@mui/icons-material/School";
import InfoIcon from "@mui/icons-material/Info";
import HistoryIcon from "@mui/icons-material/History";
import ScienceIcon from "@mui/icons-material/Science";
import StyleIcon from "@mui/icons-material/Style";

import PublicIcon from "@mui/icons-material/Public";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { CheckCircleOutline } from "@mui/icons-material";
import CalculateIcon from "@mui/icons-material/Calculate";

import WordIcon2024 from "./WordIcon2024";
import WordIcon2019 from "./WordIcon2019";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileUploader from "./FileUploader";
import WikiFetcher from "./WikiFetcher";

import UrlFetcher from "./UrlFetcher";

import Randomizer from "./Randomizer";
import Suggestions from "./Suggestions";
import CustomThemeInput from "./CustomThemeInput";

import WikipediaIcon from "./WikipediaIcon";
import TranslateIcon from "@mui/icons-material/Translate";
import LanguageIcon from "@mui/icons-material/Language";
import NatureIcon from "@mui/icons-material/Nature";

import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";

import WavesIcon from "@mui/icons-material/Waves";
import PeopleIcon from "@mui/icons-material/People";

import { getTrackedLessons } from "./functions";
import LessonCard from "./LessonCard";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import MosqueIcon from "@mui/icons-material/Mosque";
import YouTubeIcon from "@mui/icons-material/YouTube";
import BiotechIcon from "@mui/icons-material/Biotech";
import ThemeFlowBox from "./ThemeFlowBox";
import { CoursePlanSelector } from "./CoursePlanSelector";
import { YouTubeSelector } from "./YouTubeSelector";
import Logger from "./Logger";

import ShuffleIcon from "@mui/icons-material/Shuffle";

import MusicNoteIcon from "@mui/icons-material/MusicNote";
import HandymanIcon from "@mui/icons-material/Handyman";
import KitchenIcon from "@mui/icons-material/Kitchen";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import BrushIcon from "@mui/icons-material/Brush";

const StyledTabIcon = ({ icon, selected }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: selected ? "2.3rem" : "inherit",
        color: selected ? "primary.main" : "inherit",
        backgroundColor: selected ? "#7c60f41a" : "transparent",
        borderRadius: 3,
        padding: selected ? "7px" : 0,
        transition: "all 0.2s ease-in-out",
      }}
    >
      {icon}
    </Box>
  );
};

//

const ThemeSelectorFlow = ({
  value,
  onChange,
  customTheme,

  onCustomThemeChange,

  onPastedTextChange,
  onPredefinedThemeChange,
  additionalThemes = [],

  onYoutubeTranscriptChange,
  onFileUpload,
  onWikipediaContentChange,
  onRandomizedContentChange,
  onCoursePlanContentChange,
  onBookContentChange,
  onUrlContentChange,
  onAnalysisContentChange,
  lessonType,
  onLessonSelect, // New callback prop for handling lesson selection
  user, // Need to pass the user prop for getTrackedLessons
  theme,
  styles,
  forQuiz = false,
  forFlashcards = false,
  forPowerPoint = false,
  forTextlabbet = false,
  forLessonPlan = false,
  forPodcast = false,
  forTeachers = false,
  subject,
  customThemePlaceholderText,
}) => {
  const inputRef = useRef(null);
  const inputRefPasteText = useRef(null);
  const [step, setStep] = React.useState(1);
  const [selectedSource, setSelectedSource] = React.useState("customTheme");

  const textFieldRef = useRef(null);
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [allThemes, setAllThemes] = useState(additionalThemes);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [isShimmering, setIsShimmering] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [textLength, setTextLength] = useState(0);
  const { showSnackbar } = useSnackbar();
  const [showInitialButtons, setShowInitialButtons] = useState(true);

  // Add this handler function:
  const handleInitialOptionSelect = (source) => {
    setShowInitialButtons(false);
    setSelectedSource(source);
    handleSourceSelect(source);
  };

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [summaryContent, setSummaryContent] = useState(null);

  const handleContentSelection = (content, source) => {
    // Skapa en sammanfattning baserad på källan och innehållet
    let summary = "";

    if (source === "customTheme") {
      summary =
        content.length > 100 ? `${content.substring(0, 100)}...` : content;
    } else if (source === "fileUpload") {
      summary = "Uppladdad fil: " + (content.filename || "Dokument");
    } else if (source === "wikipedia") {
      summary = "Wikipedia: " + content.title;
    } else if (source === "url") {
      summary = "Webbsida: " + (content.title || content.url);
    } else if (source === "vision") {
      summary = "Bildanalys";
    } else if (source === "subject") {
      summary = `Kursplan: ${content.name || content}`;
    } else {
      summary = "Valt underlag: " + source;
    }

    setSummaryContent(summary);
    setIsCollapsed(true);
  };

  const [coursePlanSuccess, setCoursePlanSuccess] = useState(false);

  const schoolSubjects = [
    { value: "historia", label: "Historia", Icon: HistoryIcon },
    { value: "fysik", label: "Fysik", Icon: WavesIcon },
    { value: "biologi", label: "Biologi", Icon: NatureIcon },
    { value: "geografi", label: "Geografi", Icon: PublicIcon },
    { value: "engelska", label: "Engelska", Icon: LanguageIcon },
    { value: "kemi", label: "Kemi", Icon: ScienceIcon },
    { value: "modersmål", label: "Modersmål", Icon: PublicIcon },
    { value: "religionskunskap", label: "Religionskunskap", Icon: MosqueIcon },
    { value: "samhällskunskap", label: "Samhällskunskap", Icon: PeopleIcon },
    { value: "svenska", label: "Svenska", Icon: TranslateIcon },
    { value: "matematik", label: "Matematik", Icon: CalculateIcon },
    { value: "bild", label: "Bild", Icon: BrushIcon },

    {
      value: "so",
      label: "SO",
      Icon: PeopleIcon, // Fitness center icon works well for physical education
    },
    {
      value: "no",
      label: "NO",
      Icon: ScienceIcon, // Fitness center icon works well for physical education
    },

    { value: "teknik", label: "Teknik", Icon: BiotechIcon },
    {
      value: "musik",
      label: "Musik",
      Icon: MusicNoteIcon, // Music note icon is perfect for music class
    },
    {
      value: "Slöjd",
      label: "Slöjd",
      Icon: HandymanIcon, // Handyman icon works well for crafts/woodworking
    },
    {
      value: "hemkunskap",
      label: "Hem- och konsumentkunskap",
      Icon: KitchenIcon, // Kitchen icon is fitting for home economics
    },
    {
      value: "idrott och hälsa",
      label: "Idrott och hälsa",
      Icon: FitnessCenterIcon, // Fitness center icon works well for physical education
    },

    {
      value: "moderna språk",
      label: "Moderna språk",
      Icon: PublicIcon, // Fitness center icon works well for physical education
    },
  ];

  const validSchoolSubjects = schoolSubjects.map((subject) => subject.value);
  const hasCustomThemes = additionalThemes.length > 0;

  useEffect(() => {
    if (selectedSource === "lessonBank") {
      loadLessons();
    }
  }, [selectedSource]);

  useEffect(() => {
    Logger.log("subject in ThemeSelectorFlow: ", subject);
  }, [subject]);

  useEffect(() => {
    handleSourceSelect("customTheme");
  }, []);

  const loadLessons = async () => {
    setIsLoading(true);
    try {
      console.log("lessonType in loadLessons", lessonType);
      const trackedLessons = await getTrackedLessons(user.uid, lessonType);
      setLessons(trackedLessons);
    } catch (error) {
      console.error("Error loading lessons:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLessonSelect = (lesson) => {
    if (onLessonSelect) {
      onLessonSelect(lesson);
      onChange({ target: { value: "lessonBank" } });
    }
  };

  useEffect(() => {
    if (customTheme?.startsWith("Ämne:") && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(
          customTheme.length,
          customTheme.length,
        );
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [customTheme]);

  useEffect(() => {
    // Om value är ett skolämne, sätt selectedSource till 'subject'
    if (validSchoolSubjects.includes(value)) {
      setSelectedSource("subject");
      setStep(2);
    }
  }, [value]);

  const handleSourceSelect = (source) => {
    setSelectedSource(source);
    if (source === "customTheme") {
      onChange({ target: { value: "customTheme" } });
    }
    if (source === "pasteText") {
      onChange({ target: { value: "pasteText" } });
    } else if (source === "youtube") {
      onChange({ target: { value: "youtube" } });
    } else if (source === "urplay") {
      onChange({ target: { value: "urplay" } });
    } else if (source === "lessonBank") {
      onChange({ target: { value: "lessonBank" } });
    } else if (source === "predefined") {
      onChange({ target: { value: "predefined" } });
    } else if (source === "fileUpload") {
      onChange({ target: { value: "fileUpload" } });
    } else if (source === "wikipedia") {
      onChange({ target: { value: "wikipedia" } });
    } else if (source === "randomizer") {
      onChange({ target: { value: "randomizer" } });
    } else if (source === "subject") {
      onChange({ target: { value: "subject" } });
    } else if (source === "book") {
      onChange({ target: { value: "book" } });
    } else if (source === "url") {
      onChange({ target: { value: "url" } });
    } else if (source === "so-rummet") {
      onChange({ target: { value: "url" } });
    } else if (source === "vision") {
      onChange({ target: { value: "vision" } });
    }
    //setStep(2);
  };

  const handleSubjectSelect = (subject) => {
    onChange({ target: { value: subject } });
  };

  const handleSelect = (selected) => {
    if (selected.text && inputRef.current) {
      inputRef.current.value = selected.value;

      // onCustomThemeChange({ target: { value: selected.value } });
    }
  };

  const handlePredefinedThemeSelect = (topic) => {
    const selectedTheme = allThemes.find((theme) => theme.value === topic);
    onPredefinedThemeChange({
      target: {
        value: selectedTheme
          ? `${selectedTheme.label}: ${selectedTheme.content}`
          : topic,
      },
    });
  };

  return (
    <>
      <Box sx={{ ...styles.headlineLessonBox, mt: 8 }}>
        <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
          <span>1</span> {forTextlabbet ? "Ange din text" : "Välj underlag"}
        </Typography>
      </Box>
      {!showInitialButtons && (
        <IconButton
          variant="purp"
          onClick={() => {
            setShowInitialButtons(true);
          }}
          sx={{
            ...styles.backButtonNew,
            left: { xs: "60px", md: "0px" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}
      <>
        <Box
          sx={{
            ...styles.freshBox,
            ...styles.softBoxShadow,
            borderRadius: 2,
            padding: "0px",
            width: {
              xs: "100%",
              md: user.accountType === "teacher" ? "100%" : "100%",
            },
            maxWidth: "1200px",
            margin: "0 auto",
            mt: 2,
            mb: 8,
          }}
        >
          {showInitialButtons ? (
            <>
              <AnimatedStep isRevealed={true}>
                <ThemeSelectorButtons
                  theme={theme}
                  styles={styles}
                  forTextlabbet={forTextlabbet}
                  forTeachers={forTeachers}
                  user={user}
                  onOptionSelect={handleInitialOptionSelect}
                />
              </AnimatedStep>
            </>
          ) : (
            <>
              <AnimatedStep isRevealed={true} key="content-step">
                <Box
                  sx={{
                    display: "flex",
                    minHeight: 300,
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <Box
                    sx={{
                      borderRight: isMobile ? 0 : 1,
                      borderBottom: isMobile ? 1 : 0,
                      width: isMobile ? "100%" : 70,
                      borderColor: "divider",
                      background:
                        theme.palette.mode === "dark" ? "#22303c" : "#f9f9fa",
                      borderTopLeftRadius: "7px",
                      borderTopRightRadius: isMobile ? "7px" : "0px",
                      borderBottomLeftRadius: isMobile ? "0px" : "7px",
                    }}
                  >
                    {/* Replace the existing Tabs component with this version */}
                    <Tabs
                      orientation={isMobile ? "horizontal" : "vertical"}
                      value={selectedSource || "customTheme"}
                      onChange={(e, newValue) => {
                        setSelectedSource(newValue);
                        handleSourceSelect(newValue);
                      }}
                      variant={isMobile ? "scrollable" : "standard"}
                      scrollButtons="auto"
                      TabIndicatorProps={{
                        sx: {
                          left: isMobile ? undefined : 0,
                          right: isMobile ? undefined : "auto",
                          width: isMobile ? "auto" : 0,
                          backgroundColor: "primary.main",
                          borderRadius: 1,
                        },
                      }}
                      sx={{
                        "& .MuiTab-root": {
                          textTransform: "none",
                          minWidth: isMobile ? "auto" : "auto",
                          fontSize: "0.95rem",
                          alignItems: "center",
                          justifyContent: isMobile ? "center" : "center",
                          minHeight: isMobile
                            ? "70px !important"
                            : "60px !important",
                          height: isMobile
                            ? "48px !important"
                            : "60px !important",
                          position: "relative",
                          overflow: "hidden",
                          padding: isMobile ? undefined : "8px 12px",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#192734"
                                : "white",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: -50,
                            left: -100,
                            width: "70px",
                            height: "800px",
                            background:
                              theme.palette.mode === "dark"
                                ? "rgba(60, 60, 60, 0.1)"
                                : "rgba(255, 255, 255, 0.3)",
                            transform: "rotate(0deg)",
                            animation: "shimmerTabEffect 0.7s linear",
                            animationDelay: "1s",
                            zIndex: 1,
                            pointerEvents: "none",
                          },
                        },
                        "& .MuiTab-wrapper": {
                          minHeight: isMobile
                            ? "48px !important"
                            : "60px !important",
                          height: isMobile
                            ? "48px !important"
                            : "60px !important",
                        },
                        "& .Mui-selected": {
                          backgroundColor:
                            theme.palette.mode === "dark" ? "#192734" : "white",
                          paddingLeft: { xs: "15px", md: "11px" },
                          fontWeight: "600",
                        },
                        "& .Mui-selected .MuiSvgIcon-root": {
                          fontSize: "2.3rem",
                          color: "primary.main",
                          backgroundColor: "#7c60f41a",
                          borderRadius: 3,
                          padding: "7px",
                          mr: isMobile ? 1.5 : 0,
                        },
                        "@keyframes shimmerTabEffect": {
                          "0%": {
                            left: -100,
                          },
                          "100%": {
                            left: "100%",
                          },
                        },
                      }}
                    >
                      {/* Custom Theme */}
                      {!forTeachers && (
                        <Tooltip
                          title={
                            !isMobile
                              ? forTextlabbet
                                ? "Ange din text"
                                : "Egna instruktioner"
                              : ""
                          }
                          placement="right"
                        >
                          <Tab
                            value="customTheme"
                            icon={
                              <StyledTabIcon
                                icon={<EditIcon />}
                                selected={selectedSource === "customTheme"}
                              />
                            }
                            label={
                              isMobile
                                ? forTextlabbet
                                  ? "Ange din text"
                                  : "Egna instruktioner"
                                : ""
                            }
                            iconPosition="start"
                          />
                        </Tooltip>
                      )}
                      // File Upload Tab
                      <Tooltip
                        title={!isMobile ? "Ladda upp eget material" : ""}
                        placement="right"
                      >
                        <Tab
                          value="fileUpload"
                          icon={
                            <StyledTabIcon
                              icon={<FileUploadIcon />}
                              selected={selectedSource === "fileUpload"}
                            />
                          }
                          label={isMobile ? "Ladda upp eget material" : ""}
                          iconPosition="start"
                        />
                      </Tooltip>
                      {/* Paste Text */}
                      <Tooltip
                        title={!isMobile ? "Klistra in text" : ""}
                        placement="right"
                      >
                        <Tab
                          value="pasteText"
                          icon={
                            <StyledTabIcon
                              icon={<SubjectIcon />}
                              selected={selectedSource === "pasteText"}
                            />
                          }
                          label={isMobile ? "Klistra in text" : ""}
                          iconPosition="start"
                        />
                      </Tooltip>
                      {/* URL */}
                      {!forTextlabbet && !forTeachers && (
                        <Tooltip
                          title={!isMobile ? "Artikel från Wikipedia" : ""}
                          placement="right"
                        >
                          <Tab
                            value="url"
                            icon={
                              <StyledTabIcon
                                icon={<WikipediaIcon />}
                                selected={selectedSource === "url"}
                              />
                            }
                            label={isMobile ? "Webbadress (URL)" : ""}
                            iconPosition="start"
                          />
                        </Tooltip>
                      )}
                      {/* SO-rummet */}
                      {!forTextlabbet && (
                        <Tooltip
                          title={!isMobile ? "Artikel från SO-rummet" : ""}
                          placement="right"
                        >
                          <Tab
                            value="so-rummet"
                            sx={{}}
                            icon={
                              <StyledTabIcon
                                icon={
                                  <Box
                                    component="img"
                                    src="/img/so-logga.png"
                                    alt="SO-rummet"
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      objectFit: "contain",
                                    }}
                                  />
                                }
                                selected={selectedSource === "so-rummet"}
                              />
                            }
                            label={isMobile ? "SO-rummet" : ""}
                            iconPosition="start"
                          />
                        </Tooltip>
                      )}
                      {/* Vision */}
                      {!forTextlabbet && (
                        <Tooltip
                          title={!isMobile ? "Ladda upp bild" : ""}
                          placement="right"
                        >
                          <Tab
                            value="vision"
                            icon={
                              <StyledTabIcon
                                icon={<AddPhotoAlternateIcon />}
                                selected={selectedSource === "vision"}
                              />
                            }
                            label={isMobile ? "Ladda upp bild" : ""}
                            iconPosition="start"
                          />
                        </Tooltip>
                      )}
                      {/* YouTube */}
                      {!forTextlabbet && !forTeachers && (
                        <Tooltip
                          title={!isMobile ? "YouTube-video" : ""}
                          placement="right"
                        >
                          <Tab
                            value="youtube"
                            icon={
                              <StyledTabIcon
                                icon={<YouTubeIcon />}
                                selected={selectedSource === "youtube"}
                              />
                            }
                            label={isMobile ? "YouTube-video" : ""}
                            iconPosition="start"
                          />
                        </Tooltip>
                      )}
                      {/* Subject */}
                      {!forTextlabbet && !forTeachers && (
                        <Tooltip
                          title={
                            !isMobile
                              ? subject
                                ? `Kursplan — ${subject.charAt(0).toUpperCase() + subject.slice(1)}`
                                : "Skolverkets kursplan"
                              : ""
                          }
                          placement="right"
                        >
                          <Tab
                            value="subject"
                            sx={{
                              display:
                                user.accountType === "student"
                                  ? "none"
                                  : "flex",
                            }}
                            icon={
                              <StyledTabIcon
                                icon={
                                  <Box
                                    component="img"
                                    src="/img/skolverkets.jpg"
                                    alt="UR Play"
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      objectFit: "contain",
                                    }}
                                  />
                                }
                                selected={selectedSource === "subject"}
                              />
                            }
                            label={
                              isMobile
                                ? subject
                                  ? `Kursplan — ${subject.charAt(0).toUpperCase() + subject.slice(1)}`
                                  : "Skolverkets kursplan"
                                : ""
                            }
                            iconPosition="start"
                          />
                        </Tooltip>
                      )}
                      {/* Wikipedia */}
                      {!forTextlabbet && (
                        <Tooltip
                          title={!isMobile ? "Wikipedia-artikel" : ""}
                          placement="right"
                        >
                          <Tab
                            value="wikipedia"
                            icon={
                              <StyledTabIcon
                                icon={<WikipediaIcon />}
                                selected={selectedSource === "wikipedia"}
                              />
                            }
                            label={isMobile ? "Wikipedia-artikel" : ""}
                            iconPosition="start"
                            sx={{ display: "none" }}
                          />
                        </Tooltip>
                      )}
                      {/* Predefined */}
                      <Tooltip
                        title={!isMobile ? "Utvalda teman" : ""}
                        placement="right"
                      >
                        <Tab
                          value="predefined"
                          icon={
                            <StyledTabIcon
                              icon={<AutoAwesomeIcon />}
                              selected={selectedSource === "predefined"}
                            />
                          }
                          label={isMobile ? "Utvalda teman" : ""}
                          iconPosition="start"
                          sx={{ display: "none" }}
                        />
                      </Tooltip>
                      {/* Book */}
                      {!forTextlabbet && (
                        <Tooltip
                          title={!isMobile ? "Kulturrådets läslistor" : ""}
                          placement="right"
                        >
                          <Tab
                            sx={{ display: "none" }}
                            value="book"
                            icon={
                              <StyledTabIcon
                                icon={
                                  <Box
                                    component="img"
                                    src="/img/kulturradet-liten.png"
                                    alt="UR Play"
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      objectFit: "contain",
                                    }}
                                  />
                                }
                                selected={selectedSource === "book"}
                              />
                            }
                            label={isMobile ? "Kulturrådets läslistor" : ""}
                            iconPosition="start"
                          />
                        </Tooltip>
                      )}
                      <Tab
                        value="randomizer"
                        icon={
                          <StyledTabIcon
                            icon={<ShuffleIcon />}
                            selected={selectedSource === "randomizer"}
                          />
                        }
                        label="Slumpa ämne"
                        iconPosition="start"
                        sx={{ display: "none" }}
                      />
                      {/*
              {!forTextlabbet && (
                <Tab
                  value="urplay"
                  icon={
                    <Box
                      component="img"
                      src="/img/urplaysmall.png"
                      alt="UR Play"
                      sx={{
                        width: 20,
                        height: 20,
                        objectFit: "contain",
                      }}
                    />
                  }
                  label="UR Play"
                  iconPosition="start"
                />
              )}
              */}
                      <Tab
                        value="randomizer"
                        icon={<ShuffleIcon />}
                        label="Slumpa ämne"
                        iconPosition="start"
                        sx={{ display: "none" }}
                      />
                    </Tabs>
                  </Box>
                  {/*
 <VideoGuideDialog
                videoUrl="https://sportkuriren.se/wp-content/uploads/underlag.mp4"
                posterUrl="https://sportkuriren.se/wp-content/uploads/underlag.png"
                iconSize="2rem"
                autoPlay={true}
              />
*/}
                  <Box sx={{ p: 1, flexGrow: 1 }}>
                    <SourceDisplay
                      selectedSource={selectedSource}
                      forTextlabbet={forTextlabbet}
                      theme={theme}
                    />
                    <Box sx={{ p: { xs: 0, md: 3 } }}>
                      {selectedSource === "customTheme" && (
                        <>
                          <Box
                            sx={{
                              pt: { xs: 0, md: 5 },
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                            }}
                          >
                            <Box
                              component="img"
                              sx={{
                                width: "10%",
                                opacity: 0.8,
                                position: "absolute",
                                top: 10,
                                display: { xs: "none", md: "block" },
                              }}
                              src="/img/curved-arrow.png"
                            />
                            <Box
                              sx={{
                                width: { xs: "100%", md: "80%" },
                                ml: { xs: 0, md: 14 },
                                mt: 4,
                              }}
                            >
                              <TextField
                                fullWidth
                                defaultValue={customTheme}
                                onBlur={(e) => {
                                  console.log(
                                    "TextField value on blur:",
                                    e.target.value,
                                  );
                                }}
                                placeholder={
                                  customThemePlaceholderText
                                    ? customThemePlaceholderText
                                    : forTextlabbet
                                      ? "Skriv in eller klistra in din text här"
                                      : "Vilket ämne ska det handla om?"
                                }
                                inputRef={inputRef}
                                variant="outlined"
                                multiline
                                autoFocus={!isMobile}
                                rows={forTextlabbet ? 10 : 3}
                                InputProps={{
                                  style: {
                                    fontWeight: "500",
                                    fontSize: "1.05rem",
                                  },
                                }}
                              />
                              <Box sx={{ textAlign: "right" }}>
                                <Box sx={{ display: "none" }}>
                                  <VideoGuideDialog
                                    videoUrl="https://sportkuriren.se/wp-content/uploads/underlag.mp4"
                                    posterUrl="https://sportkuriren.se/wp-content/uploads/underlag.png"
                                    iconSize="2rem"
                                    autoPlay={true}
                                    tooltip="Hur du väljer underlag"
                                  />
                                </Box>
                                <Button
                                  variant="purp"
                                  sx={{ mt: 4 }}
                                  onClick={() => {
                                    const currentValue =
                                      inputRef.current?.value || "";

                                    if (!currentValue.trim()) {
                                      showSnackbar("Välj underlag.", "info");
                                      return;
                                    }

                                    onCustomThemeChange({
                                      target: { value: currentValue },
                                    });
                                  }}
                                  endIcon={<ArrowForwardIosIcon />}
                                >
                                  Nästa
                                </Button>
                              </Box>
                              {!forQuiz &&
                                !forFlashcards &&
                                !forPowerPoint &&
                                !forTextlabbet &&
                                !forLessonPlan && (
                                  <Box sx={{ mt: 2, display: "none" }}>
                                    <Suggestions
                                      listType="lessonIdeas"
                                      maxSuggestions={6}
                                      onSelect={handleSelect}
                                    />
                                  </Box>
                                )}

                              {forQuiz && (
                                <Box sx={{ mt: 2 }}>
                                  <Suggestions
                                    listType="quizIdeas"
                                    maxSuggestions={6}
                                    onSelect={handleSelect}
                                  />
                                </Box>
                              )}
                              {forFlashcards && (
                                <Box sx={{ mt: 2 }}>
                                  <Suggestions
                                    listType="flashcardIdeas"
                                    maxSuggestions={6}
                                    onSelect={handleSelect}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </>
                      )}

                      {selectedSource === "pasteText" && (
                        <>
                          <Box
                            sx={{
                              pt: { xs: 0, md: 0 },
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                ml: { xs: 0, md: 0 },
                                mt: 2,
                              }}
                            >
                              <TextField
                                fullWidth
                                onChange={(e) => {
                                  // Update a state variable to track text length
                                  setTextLength(e.target.value.length);
                                }}
                                placeholder="Klistra in din text här..."
                                inputRef={inputRefPasteText}
                                variant="outlined"
                                multiline
                                autoFocus={!isMobile}
                                rows={8}
                                sx={{ mb: 1 }}
                                InputProps={{
                                  maxLength: "10000",
                                  style: {
                                    fontWeight: "500",
                                    fontSize: "1.05rem",
                                  },
                                }}
                              />
                              <Box display="flex" justifyContent="flex-start">
                                <Typography variant="caption">
                                  {textLength} / 10000
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: "right" }}>
                                <Button
                                  variant="purp"
                                  sx={{ mt: 4 }}
                                  onClick={() => {
                                    const currentValue =
                                      inputRefPasteText.current?.value || "";

                                    if (!currentValue.trim()) {
                                      showSnackbar(
                                        "Klistra in din text.",
                                        "info",
                                      );
                                      return;
                                    }

                                    onPastedTextChange({
                                      target: { value: currentValue },
                                    });
                                  }}
                                  endIcon={<ArrowForwardIosIcon />}
                                >
                                  Nästa
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </>
                      )}

                      {(selectedSource === "subject" ||
                        validSchoolSubjects.includes(value)) && (
                        <>
                          {coursePlanSuccess ? (
                            <Box sx={{ ...styles.successBox }}>
                              <CheckCircleOutline
                                sx={{
                                  fontSize: 60,
                                  color: "success.main",
                                  mb: 3,
                                }}
                              />
                              <Typography
                                variant="h6"
                                sx={{
                                  color: "success.main",
                                  fontWeight: 500,
                                  textAlign: "center",
                                  width: "80%",
                                  margin: "0 auto",
                                  mb: 3,
                                }}
                              >
                                Ämnet är nu valt och du kan fortsätta med att
                                generera{" "}
                                {forQuiz
                                  ? "ditt quiz"
                                  : forPowerPoint
                                    ? "din PowerPoint"
                                    : "din lektion"}{" "}
                                nedan.
                              </Typography>
                            </Box>
                          ) : !validSchoolSubjects.includes(value) &&
                            !subject ? (
                            <Grid container spacing={2}>
                              {schoolSubjects.map((subject) => (
                                <Grid
                                  item
                                  xs={12}
                                  sm={4}
                                  md={4}
                                  key={subject.value}
                                >
                                  <ThemeFlowBox
                                    icon={subject.Icon}
                                    title={subject.label}
                                    onClick={() =>
                                      handleSubjectSelect(subject.value)
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <CoursePlanSelector
                              subject={subject || value}
                              theme={theme}
                              styles={styles}
                              onTopicSelect={(content) => {
                                onCoursePlanContentChange(content);
                                setCoursePlanSuccess(true);
                              }}
                              forQuiz={forQuiz}
                              forFlashcards={forFlashcards}
                              forPowerPoint={forPowerPoint}
                            />
                          )}
                        </>
                      )}

                      {selectedSource === "lessonBank" && (
                        <>
                          {isLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                mt: 4,
                                minHeight: "276px",
                              }}
                            >
                              <CircularProgress />
                            </Box>
                          ) : lessons.length < 1 ? (
                            <Box sx={{ ...styles.successBox }}>
                              <SentimentVerySatisfiedIcon
                                sx={{
                                  fontSize: 60,
                                  color: "success.main",
                                  mb: 3,
                                }}
                              />
                              <Typography
                                variant="h6"
                                sx={{
                                  color: "success.main",
                                  fontWeight: 500,
                                  textAlign: "center",
                                  width: "80%",
                                  margin: "0 auto",
                                  mb: 3,
                                }}
                              >
                                Snart kommer det att finnas färdiga, noggrant
                                kurerade lektioner att välja mellan här. Så
                                titta in snart igen, eller testa generera en
                                lektion själv så länge! :)
                              </Typography>
                            </Box>
                          ) : (
                            <>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: "500",
                                  mb: 4,
                                  fontSize: "1rem",
                                  textAlign: "center",
                                }}
                              >
                                {forPowerPoint
                                  ? "Välj bland några utvalda presentationer som våra användare skapat. Anpassa och ladda ned!"
                                  : "Välj bland de mest poulära lektionerna som våra användare skapat för denna lektionstyp."}
                              </Typography>
                              <Grid container spacing={2}>
                                {lessons.map((lesson) => (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={lesson.id}
                                  >
                                    <Box sx={{ position: "relative" }}>
                                      <Box
                                        component={Link}
                                        to={`/app${lesson.lessonType !== "PowerPointGenerator.js" ? "/lektioner" : ""}/${lessonUrlMapping(lesson.lessonType)}/${lesson.id.substring(0, 10)}${getLessonTypeParam(lesson.lessonType) === "default" ? "" : `?type=${getLessonTypeParam(lesson.lessonType)}`}`}
                                        sx={{
                                          cursor: "pointer",
                                          textDecoration: "none",
                                          "&:hover": {
                                            opacity: 0.8,
                                          },
                                        }}
                                      >
                                        <LessonCard
                                          name={lesson.lessonName}
                                          comment={lesson.comment}
                                          timestamp={lesson.timestamp}
                                          lessonType={lesson.lessonType}
                                          imageUrl={lesson.coverImage}
                                          onClick={() =>
                                            handleLessonSelect(lesson)
                                          }
                                          size="large"
                                          centered={true}
                                          width="285px"
                                          height="260px"
                                        />
                                      </Box>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </>
                          )}
                        </>
                      )}

                      {selectedSource === "fileUpload" && (
                        <FileUploader
                          theme={theme}
                          styles={styles}
                          onFileContent={(fileContent) => {
                            onFileUpload(fileContent);
                            handleContentSelection(fileContent, "fileUpload");
                          }}
                          forQuiz={forQuiz}
                          forFlashcards={forFlashcards}
                          forPowerPoint={forPowerPoint}
                          forTextlabbet={forTextlabbet}
                        />
                      )}

                      {selectedSource === "wikipedia" && (
                        <WikiFetcher
                          styles={styles}
                          theme={theme}
                          onWikipediaChange={(content) => {
                            onWikipediaContentChange(content);
                            handleContentSelection(content, "wikipedia");
                          }}
                          forQuiz={forQuiz}
                          forFlashcards={forFlashcards}
                          forPowerPoint={forPowerPoint}
                        />
                      )}

                      {selectedSource === "url" && (
                        <UrlFetcher
                          styles={styles}
                          theme={theme}
                          onUrlContentChange={onUrlContentChange}
                          user={user}
                          type="url"
                        />
                      )}

                      {selectedSource === "vision" && (
                        <VisionSimple
                          styles={styles}
                          theme={theme}
                          onAnalysisContentChange={onAnalysisContentChange}
                          user={user}
                        />
                      )}

                      {selectedSource === "so-rummet" && (
                        <UrlFetcher
                          styles={styles}
                          theme={theme}
                          onUrlContentChange={onUrlContentChange}
                          user={user}
                          type="so-rummet"
                        />
                      )}

                      {selectedSource === "youtube" && (
                        <YouTubeSelector
                          onTranscriptChange={onYoutubeTranscriptChange}
                          subject={value}
                          forQuiz={forQuiz}
                          urplay={false}
                          forFlashcards={forFlashcards}
                          forPowerPoint={forPowerPoint}
                          forPodcast={forPodcast}
                          user={user}
                        />
                      )}
                      {selectedSource === "book" && (
                        <BookPlanSelector
                          onBookSelect={onBookContentChange}
                          styles={styles}
                          theme={theme}
                        />
                      )}

                      {selectedSource === "urplay" && (
                        <YouTubeSelector
                          subject={value}
                          onTranscriptChange={onYoutubeTranscriptChange}
                          urplay={true}
                          forQuiz={forQuiz}
                          user={user}
                          forFlashcards={forFlashcards}
                          forPowerPoint={forPowerPoint}
                        />
                      )}
                      {selectedSource === "randomizer" && (
                        <Randomizer
                          styles={styles}
                          theme={theme}
                          onSelect={onRandomizedContentChange}
                          forQuiz={forQuiz}
                          forFlashcards={forFlashcards}
                          forPowerPoint={forPowerPoint}
                          forPodcast={forPodcast}
                        />
                      )}

                      {selectedSource === "predefined" && (
                        <Box sx={{ py: 2 }}>
                          {allThemes.length > 0 && (
                            <>
                              <Grid container spacing={2}>
                                {allThemes.map((theme, index) => {
                                  if (theme.type === "subheader") {
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        key={`subheader-${index}`}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "500",
                                            mb: 2,
                                            fontSize: "1rem",
                                            textAlign: "center",
                                          }}
                                        >
                                          {theme.label}
                                        </Typography>
                                      </Grid>
                                    );
                                  }

                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={4}
                                      key={theme.value}
                                    >
                                      <ThemeFlowBox
                                        icon={theme.Icon}
                                        title={theme.label}
                                        isSelected={
                                          selectedTheme === theme.value
                                        }
                                        onClick={() => {
                                          setSelectedTheme(theme.value);
                                          handlePredefinedThemeSelect(
                                            theme.value,
                                          );
                                        }}
                                      />
                                    </Grid>
                                  );
                                })}
                              </Grid>
                              {selectedTheme && (
                                <Box sx={{ mt: 4 }}>
                                  <Box sx={{ ...styles.successBox }}>
                                    <CheckCircleOutline
                                      sx={{
                                        fontSize: 60,
                                        color: "success.main",
                                        mb: 3,
                                      }}
                                    />
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        color: "success.main",
                                        fontWeight: 500,
                                        textAlign: "center",
                                        width: "80%",
                                        margin: "0 auto",
                                        mb: 3,
                                      }}
                                    >
                                      Tema valt:{" "}
                                      {allThemes.find(
                                        (t) => t.value === selectedTheme,
                                      )?.content ||
                                        allThemes.find(
                                          (t) => t.value === selectedTheme,
                                        )?.label}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              <CustomThemeInput
                                onSubmit={(customValue) => {
                                  const newTheme = {
                                    value: `custom-${Date.now()}`,
                                    label: customValue,
                                    Icon: EditIcon,
                                    content: customValue,
                                    type: "custom",
                                  };
                                  setAllThemes([...additionalThemes, newTheme]);
                                  setSelectedTheme(newTheme.value);
                                  handlePredefinedThemeSelect(customValue);
                                }}
                                theme={theme}
                              />
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </AnimatedStep>
            </>
          )}
        </Box>
      </>
    </>
  );
};

export { ThemeSelectorFlow };
